import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { OrdersQueryKeys } from "@features/orders-features/hooks/OrdersQueryKeys";
import { GetBearerToken } from "../../../../../auth/BearerToken";
import { getImpersonateHeaders } from "../../../../../auth/ApiClientConfig";
import { fetchEventSource } from "@microsoft/fetch-event-source";

export type OrderSyncStatus = "idle" | "updating" | "success" | "error";

interface OrderSyncEvent {
  status: OrderSyncStatus;
  lastSyncedAt: string | null;
}

interface OrderSyncState {
  syncStatus: OrderSyncStatus;
  lastSyncedAt: Date | null;
}

function tryParseStatus(raw: string): OrderSyncEvent | null {
  try {
    const parsed = JSON.parse(raw);
    if (!parsed?.status) return null;
    return parsed as OrderSyncEvent;
  } catch {
    return null;
  }
}

const MAX_TOKEN_RETRIES = 3;
const RETRY_DELAY_MS = 5000;

type OrderSyncListener = (state: OrderSyncState) => void;

const initialState: OrderSyncState = {
  syncStatus: "idle",
  lastSyncedAt: null,
};

let sharedState: OrderSyncState = initialState;
const listeners = new Set<OrderSyncListener>();
let activeSubscribers = 0;
let teardownStream: (() => void) | null = null;

function emitState() {
  listeners.forEach((listener) => listener(sharedState));
}

function setSharedState(next: OrderSyncState) {
  const isSameStatus = sharedState.syncStatus === next.syncStatus;
  const currentTime = sharedState.lastSyncedAt?.getTime() ?? null;
  const nextTime = next.lastSyncedAt?.getTime() ?? null;
  if (isSameStatus && currentTime === nextTime) {
    return;
  }

  sharedState = next;
  emitState();
}

function buildHeaders(token: string | undefined): Record<string, string> {
  const {
    impersonateUserId,
    assumeIdentity,
    impersonateAccount,
    demoData,
    impersonateEmail,
  } = getImpersonateHeaders();

  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
    Accept: "text/event-stream",
  };

  if (impersonateUserId) headers.ImpersonateUserId = impersonateUserId;
  if (assumeIdentity) headers.AssumeIdentity = assumeIdentity;
  if (impersonateAccount) headers.ImpersonateAccount = impersonateAccount;
  if (demoData) headers.DemoData = demoData;
  if (impersonateEmail) headers.ImpersonateEmail = impersonateEmail;

  return headers;
}

function startOrdersSyncStream(
  baseUrl: string,
  queryClient: ReturnType<typeof useQueryClient>,
) {
  const controller = new AbortController();
  let tokenRetries = 0;
  let lastInvalidatedSuccessStamp: string | null = null;

  const connect = async () => {
    while (!controller.signal.aborted) {
      try {
        const token = await GetBearerToken();

        if (controller.signal.aborted) {
          return;
        }

        if (!token) {
          setSharedState({ syncStatus: "error", lastSyncedAt: sharedState.lastSyncedAt });
          await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));
          continue;
        }

        await fetchEventSource(`${baseUrl}/api/OrderSyncStream/sync-stream`, {
          method: "GET",
          headers: buildHeaders(token),
          signal: controller.signal,

          async onopen(response) {
            if (response.ok) {
              tokenRetries = 0;
              return;
            }

            if (response.status === 401 && tokenRetries < MAX_TOKEN_RETRIES) {
              tokenRetries++;
              throw new DOMException("Token expired", "TokenExpiredError");
            }

            throw new Error(`SSE failed: ${response.status}`);
          },

          onmessage(event) {
            const parsed = tryParseStatus(event.data);
            if (!parsed) {
              return; // heartbeat or malformed -- preserve existing state
            }

            const nextLastSyncedAt = parsed.lastSyncedAt
              ? new Date(parsed.lastSyncedAt)
              : sharedState.lastSyncedAt;

            setSharedState({
              syncStatus: parsed.status,
              lastSyncedAt: nextLastSyncedAt,
            });

            if (parsed.status === "success") {
              const invalidateStamp = parsed.lastSyncedAt ?? "success-without-timestamp";
              if (invalidateStamp !== lastInvalidatedSuccessStamp) {
                lastInvalidatedSuccessStamp = invalidateStamp;
                void queryClient.invalidateQueries({
                  queryKey: [OrdersQueryKeys.Search],
                });
              }
            }
          },

          onerror(err) {
            if (controller.signal.aborted) {
              throw err;
            }

            throw err;
          },
        });

        return;
      } catch (err) {
        if (controller.signal.aborted) {
          return;
        }

        if (err instanceof DOMException && err.name === "TokenExpiredError") {
          continue;
        }

        console.error("SSE error:", err);
        setSharedState({ syncStatus: "error", lastSyncedAt: sharedState.lastSyncedAt });
        await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));
      }
    }
  };

  void connect();

  return () => {
    controller.abort();
  };
}

export function useOrdersStream() {
  const queryClient = useQueryClient();
  const [state, setState] = useState<OrderSyncState>(sharedState);
  const baseUrl = import.meta.env.API_ENDPOINT;

  useEffect(() => {
    const listener: OrderSyncListener = (next) => {
      setState(next);
    };

    listeners.add(listener);
    listener(sharedState);

    activeSubscribers += 1;
    if (!teardownStream) {
      teardownStream = startOrdersSyncStream(baseUrl, queryClient);
    }

    return () => {
      listeners.delete(listener);
      activeSubscribers = Math.max(0, activeSubscribers - 1);

      if (activeSubscribers === 0 && teardownStream) {
        teardownStream();
        teardownStream = null;
      }
    };
  }, [baseUrl, queryClient]);

  return state;
}
